import React, { useState, useEffect } from 'react';
import { FormControlLabel, Checkbox, Typography, Box, Button, Grid, Link, Stepper, Step, StepLabel, Container, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import axios from 'axios';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { footerLinks } from '../Shared/footerLinks';
import { theme, style, config, StyledRadio } from '../../../config/settings.jsx';
import { steps } from '../../../config/steps';
import { getTheme } from '../../../config/theme';

const FundingAccounts = () => {
  const [subdomain, setSubdomain] = useState('');
  const [isLoadingTheme, setIsLoadingTheme] = useState(false);
  const [theme, setTheme] = useState({});
  const [assetsPath, setAssetsPath] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [selectedAccountObjectId, setSelectedAccountObjectId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showAgreement, setShowAgreement] = useState(false);
  const [showServiceAgreement, setShowServiceAgreement] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [stepperStep, setStepperStep] = useState(2);
  const { accounts, cdrid } = location.state || {};



  useEffect(() => {
    if (!accounts || accounts.length === 0) {
      // Redirect to a different page or show an error message
      navigate('/previous-page'); // Adjust this to your needs
    }
  }, [accounts, navigate]);

  const handleCheckboxChange = (id, _id) => {
  setSelectedAccountId(id);
  setSelectedAccountObjectId(_id);
  };
  

  const handleSubmitFundingAccount = async () => {
    setIsLoading(true);
          
    try {
      await axios.post('https://minerva.sipora.io/api/client/round-up/user/register-funding', 
        {
        accountId: selectedAccountId, 
        account_id: selectedAccountObjectId, 
        tenantA: config.tenantA, 
        activeDebitEnabled: true, 
        cdrid: cdrid
      }, { headers: { 'Content-Type': 'application/json',
          apikey: config.apikey,
            tenant: config.tenant
       } }
      
    );
      navigate('/finished', { state: {cdrid} }); // Adjust the navigation path as needed
    } catch (error) {
      console.error('Error submitting selected accounts:', error);
      setErrorMessage('There seems to be an error, please try again or contact support')
    } finally {
      setIsLoading(false);
    }
  };

  if (!accounts || accounts.length === 0) {
    return (
    
              <Container component="main" maxWidth="xs">
                
        <Box display="flex" flexDirection="column" alignItems="center" mt={4} p={2}>
          <Typography variant="h6" component="h2" align="left" mb={2}>
            No accounts available. Please go back and select accounts.
          </Typography>
          <Button
            onClick={() => navigate('/previous-page')} // Adjust this to your needs
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 3, mb: 2, borderRadius: 3, width: '100%' }}
          >
            <Typography>Go Back</Typography>
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <ThemeProvider theme={getTheme}>
    <Container component="main" maxWidth="xs">
    {isLoadingTheme ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', mt: 20 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>

      <Box display="flex" flexDirection="column" alignItems="center" mt={4} p={2}>
        <Grid container justifyContent="center" mb={2}>
          <Box display="flex" flexDirection="column" alignItems="center" mt={4} mb={6} style={{ width: '100%' }}>
            <Stepper activeStep={stepperStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
          <Typography variant="h4" component="h2" align="left" mb={2} sx={{ fontWeight: 'bold' }} gutterBottom>
            Select Your Funding Account
          </Typography>
          <Box display="flex" flexDirection="column" bgcolor="white" mt={1} p={1.5} borderRadius={4} sx={{ width: '100%', pb: 0.5 }}>
            {accounts.map((account, index) => (
              <FormControlLabel
                key={index}
                control={
                  <Checkbox
                    checked={selectedAccountId === account.id}
                    onChange={() => handleCheckboxChange(account.id, account._id)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">{account.name} {account.accountNumber}</Typography>
                  </Box>
                }
              />
            ))}
          </Box>

          <Box display="flex" flexDirection="column" alignItems="left" bgcolor="white" mt={0} p={2} borderRadius={4}>
            <Typography variant="body2" align="left" sx={{ mb: 1 }}>
              Your funding account will have your round-ups direct debited on Monday, Wednesday & Friday.
            </Typography>
            <Typography variant="body2" align="left" sx={{ mb: 1.5 }}>
              By selecting a funding account, you are agreeing to Zai's terms and conditions which can be found here:
            </Typography>
            <Typography variant="body2" align="left" sx={{ mb: 0.5 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => setShowAgreement(true)}
                sx={{ verticalAlign: 'baseline', textAlign: 'left' }}
              >
                Zai Direct Debit Request
              </Link>
            </Typography>
            <Typography variant="body2" align="left">
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => setShowServiceAgreement(true)}
                sx={{ verticalAlign: 'baseline', textAlign: 'left' }}
              >
                Zai Direct Debit Request Service Agreement
              </Link>
            </Typography>
            <Dialog
              open={showAgreement}
              onClose={() => setShowAgreement(false)}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle sx={{ fontWeight: 'bold' }}>Zai Direct Debit Request</DialogTitle>
              <DialogContent dividers>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                  <Box component="img" src={`${process.env.PUBLIC_URL}/cents2.png`} alt="Cents2" sx={{ height: 72 }} />
                </Box>
                <Typography variant="body2" align="center">
                  ABN <Link href="https://abr.business.gov.au/ABN/View?abn=18669434995" target="_blank" rel="noopener">18 669 434 995</Link>
                </Typography>
                <Typography variant="body2" align="center">
                  Suite 57/10-20 Gwynne Street, Cremorne, 3121 Vic
                </Typography>
                <Typography variant="body2" align="center" sx={{ mb: 2 }}>
                  <Link href="mailto:member@cents2.com.au">member@cents2.com.au</Link>
                </Typography>
                <Typography variant="body2" align="left" gutterBottom>
                  This is your Direct Debit Service Agreement with Zai Australia Pty Ltd (Zai), User ID's 342203 and 481561, ABN: 96637632645, (the Debit User), as agent for PokitPal Pty Ltd (ABN: 49 131 686 167) to arrange a debit to your nominated account to pay for Round-Ups related goods and services provided by Cents2 as per the DDRSA.
                </Typography>
                <Typography variant="body2" align="left" gutterBottom>
                  This debit or charge will be arranged by Zai's financial institution and made through the Bulk Electronic Clearing System Framework (BECS) from your nominated account and will be subject to the terms and conditions of the Direct Debit Request Service Agreement.
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowAgreement(false)}>Close</Button>
              </DialogActions>
            </Dialog>
            <Dialog
              open={showServiceAgreement}
              onClose={() => setShowServiceAgreement(false)}
              maxWidth="sm"
              fullWidth
              scroll="paper"
            >
              <DialogTitle sx={{ fontWeight: 'bold' }}>Direct Debit Request Service Agreement</DialogTitle>
              <DialogContent dividers>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                  <Box component="img" src={`${process.env.PUBLIC_URL}/cents2.png`} alt="Cents2" sx={{ height: 72 }} />
                </Box>
                <Typography variant="body2" align="center">
                  ABN <Link href="https://abr.business.gov.au/ABN/View?abn=18669434995" target="_blank" rel="noopener">18 669 434 995</Link>
                </Typography>
                <Typography variant="body2" align="center">
                  Suite 57/10-20 Gwynne Street, Cremorne, 3121 Vic
                </Typography>
                <Typography variant="body2" align="center" sx={{ mb: 2 }}>
                  <Link href="mailto:member@cents2.com.au">member@cents2.com.au</Link>
                </Typography>

                <Typography variant="body2" sx={{ mt: 2 }} gutterBottom>
                  This is your Direct Debit Service Agreement with Zai Australia Pty Ltd (Zai), User ID's 342203 and 481561, ABN: 96637632645, (the Debit User), as agent for PokitPal Pty Ltd (ABN: 49 131 686 167) to arrange a debit to your nominated account to pay for Round-Ups related goods and services provided by Cents2.
                </Typography>
                <Typography variant="body2" gutterBottom>
                  This debit or charge will be arranged by Zai's financial institution and made through the Bulk Electronic Clearing System Framework (BECS) from your nominated account and will be subject to the terms and conditions of the Direct Debit Request Service Agreement.
                </Typography>
                <Typography variant="body2" gutterBottom>
                  It explains what your obligations are when undertaking a Direct Debit arrangement with us. It also details what our obligations are to you as your Direct Debit provider.
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Please keep this agreement for future reference. It forms part of the terms and conditions of your Direct Debit Request (DDR) and should be read in conjunction with your DDR authorisation.
                </Typography>

                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2 }} gutterBottom>Definitions</Typography>
                <Typography variant="body2" gutterBottom><strong>account</strong> means the account held at your financial institution from which we are authorised to arrange for funds to be debited.</Typography>
                <Typography variant="body2" gutterBottom><strong>agreement</strong> means this Direct Debit Request Service Agreement between you and us.</Typography>
                <Typography variant="body2" gutterBottom><strong>banking day</strong> means a day other than a Saturday or a Sunday or a public holiday listed throughout Australia.</Typography>
                <Typography variant="body2" gutterBottom><strong>debit day</strong> means the day that payment by you to us is due.</Typography>
                <Typography variant="body2" gutterBottom><strong>debit payment</strong> means a particular transaction where a debit is made.</Typography>
                <Typography variant="body2" gutterBottom><strong>direct debit request</strong> means the Direct Debit Request between us and you.</Typography>
                <Typography variant="body2" gutterBottom><strong>us or we</strong> means Zai Australia Pty Ltd (the Debit User), acting on behalf of PokitPal whom you have authorised by requesting a Direct Debit Request.</Typography>
                <Typography variant="body2" gutterBottom><strong>you</strong> means the customer who has signed or authorised by other means the Direct Debit Request.</Typography>
                <Typography variant="body2" gutterBottom><strong>your financial institution</strong> means the financial institution nominated by you on the DDR at which the account is maintained.</Typography>
                <Typography variant="body2" gutterBottom><strong>Cents2</strong> means the party that provides the services you are paying for, by way of requesting us to process your valid Debit payment instructions.</Typography>
                <Typography variant="body2" gutterBottom><strong>PokitPal</strong> means the provider of the Round-Ups service that collects valid instructions authorised by you. They provide these instructions to us to enable the processing of related debit payments instructions.</Typography>

                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2 }} gutterBottom>Debiting your account</Typography>
                <Typography variant="body2" gutterBottom>1.1 By submitting a Direct Debit Request, you have authorised us to arrange for funds to be debited from your account. The Direct Debit Request and this agreement set out the terms of the arrangement between us and you.</Typography>
                <Typography variant="body2" gutterBottom>1.2 We will only arrange for funds to be debited from your account as authorised in the Direct Debit Request.</Typography>
                <Typography variant="body2" gutterBottom>1.3 If the debit day falls on a day that is not a banking day, we may direct your financial institution to debit your account on the following banking day. If you are unsure about which day your account has or will be debited you should ask your financial institution.</Typography>

                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2 }} gutterBottom>Amendments by us</Typography>
                <Typography variant="body2" gutterBottom>2.1 We may vary any details of this agreement or a Direct Debit Request at any time by giving you at least 30 days written notice sent to the preferred email or address you have given us in the Direct Debit Request.</Typography>

                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2 }} gutterBottom>Amendments by you</Typography>
                <Typography variant="body2" gutterBottom>3.1 You can at any time by giving us 5 days' notice:</Typography>
                <Typography variant="body2" gutterBottom>(a) cancel or suspend the Direct Debit Request; or</Typography>
                <Typography variant="body2" gutterBottom>(b) change, stop or defer an individual debit payment;</Typography>
                <Typography variant="body2" gutterBottom>To do so, you can:</Typography>
                <Typography variant="body2" gutterBottom>(i) update your instructions using the PokitPal software application. PokitPal will notify us and we will act on your instructions; OR</Typography>
                <Typography variant="body2" gutterBottom>
                  (ii) to cancel your Direct Debit Request, notify us directly via email to: <Link href="mailto:helpdesk+pokitpal@hellozai.com">helpdesk+pokitpal@hellozai.com</Link> or by mail to: <em>Zai Australia Pty Ltd, Attention: <strong>PokitPal</strong> Support Team, The Commons, Level 1, 55 Collins Street, Melbourne, VIC 3000</em>
                </Typography>
                <Typography variant="body2" gutterBottom>You can also contact your own financial institution, which must act promptly on your instructions.</Typography>

                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2 }} gutterBottom>Your obligations</Typography>
                <Typography variant="body2" gutterBottom>4.1 It is your responsibility to ensure that there are sufficient clear funds available in your account to allow a debit payment to be made in accordance with the Direct Debit Request.</Typography>
                <Typography variant="body2" gutterBottom>4.2 If there are insufficient clear funds in your account to meet a debit payment:</Typography>
                <Typography variant="body2" gutterBottom>(a) you may be charged a fee and/or interest by your financial institution;</Typography>
                <Typography variant="body2" gutterBottom>(b) we may charge you reasonable costs incurred by us on account of there being insufficient funds; and</Typography>
                <Typography variant="body2" gutterBottom>(c) you must arrange for the debit payment to be made by another method or arrange for sufficient clear funds to be in your account by an agreed time so that we can process the debit payment.</Typography>
                <Typography variant="body2" gutterBottom>4.3 You should check your account statement to verify that the amounts debited from your account are correct.</Typography>

                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2 }} gutterBottom>Dispute</Typography>
                <Typography variant="body2" gutterBottom>
                  5.1 If you believe that there has been an error in debiting your account, you should notify us directly at <Link href="mailto:helpdesk+pokitpal@hellozai.com">helpdesk+pokitpal@hellozai.com</Link> or by mail to: <em>Zai Australia Pty Ltd, Attention: <strong>PokitPal</strong> Support Team, The Commons, Level 1, 55 Collins Street, Melbourne VIC 3000.</em> Alternatively, you can contact your financial institution and/or PokitPal assistance.
                </Typography>
                <Typography variant="body2" gutterBottom>5.2 If we conclude as a result of our investigations that your account has been incorrectly debited we will respond to your query by arranging within a reasonable period, for your financial institution to adjust your account (including interest and charges) accordingly. We will also notify you in writing of the amount by which your account has been adjusted.</Typography>
                <Typography variant="body2" gutterBottom>5.3 If we conclude as a result of our investigations that your account has not been incorrectly debited we will respond to your query by providing you with reasons and any evidence for this finding in writing.</Typography>

                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2 }} gutterBottom>Accounts</Typography>
                <Typography variant="body2" gutterBottom>6.1 You should check:</Typography>
                <Typography variant="body2" gutterBottom>(a) with your financial institution whether direct debiting is available from your account as direct debiting is not available on all accounts offered by financial institutions.</Typography>
                <Typography variant="body2" gutterBottom>(b) your account details which you have provided to us are correct by checking them against a recent account statement; and</Typography>
                <Typography variant="body2" gutterBottom>(c) with your financial institution before completing the Direct Debit Request if you have any queries about how to complete the Direct Debit Request.</Typography>
                <Typography variant="body2" gutterBottom>6.2 While the account you nominate for this purpose may be jointly held by more parties than those signing the Direct Debit Request, you must nominate an account that your Financial Institution permits you to operate including the permission to authorise the debiting of that account, without your Financial Institution requiring additional party to also authorise such debit payments.</Typography>
                <Typography variant="body2" gutterBottom>6.3 If you have identified yourself as an individual only in the Direct Debit Request, for the purposes of the Direct Debit Request, you may not nominate an Account issued to or operated by a company or another similar type of entity structure, such as a Trust.</Typography>

                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2 }} gutterBottom>Confidentiality</Typography>
                <Typography variant="body2" gutterBottom>7.1 We will keep any information (including your account details) in your Direct Debit Request confidential. We will make reasonable efforts to keep any such information that we have about you secure and to ensure that any of our employees or agents who have access to information about you do not make any unauthorised use, modification, reproduction or disclosure of that information.</Typography>
                <Typography variant="body2" gutterBottom>7.2 We will only disclose information that we have about you:</Typography>
                <Typography variant="body2" gutterBottom>(a) to the extent specifically required by law; or</Typography>
                <Typography variant="body2" gutterBottom>(b) for the purposes of this agreement (including disclosing information in connection with any query or claim).</Typography>

                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2 }} gutterBottom>Notice</Typography>
                <Typography variant="body2" gutterBottom>
                  8.1 If you wish to notify us in writing about anything relating to this agreement, you should email us at <Link href="mailto:helpdesk+pokitpal@hellozai.com">helpdesk+pokitpal@hellozai.com</Link> or mail us at: <em>Zai Australia Pty Ltd, Attention: <strong>PokitPal</strong> Support Team, The Commons, Level 1, 55 Collins Street, Melbourne VIC 3000.</em>
                </Typography>
                <Typography variant="body2" gutterBottom>8.2 We will notify you by sending a notice to the preferred contact method you have given us in the Direct Debit Request.</Typography>
                <Typography variant="body2" gutterBottom>8.3 Any notice will be deemed to have been received on the second banking day after sending if sent via email, or on the fifth banking day if sent via post.</Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowServiceAgreement(false)}>Close</Button>
              </DialogActions>
            </Dialog>
          </Box>

          <Box display="flex" flexDirection="column" bgcolor="white" mt={1} p={2.5} borderRadius={4} sx={{ width: '100%', pb: 1 }}>
            <Typography variant="body2" align="left" sx={{ fontWeight: 'bold', mb: 1 }}>
              Amount of debit
            </Typography>
            <Typography variant="body2" align="left" gutterBottom>
              Any amount Cents2 has deemed payable by you.
            </Typography>
            <Typography variant="body2" align="left" sx={{ fontWeight: 'bold', mt: 2.5, mb: 1 }}>
              Acknowledgment
            </Typography>
            <Typography variant="body2" align="left" gutterBottom>
              By signing and/or providing us with a valid electronic instruction in respect to your Direct Debit Request you confirm that:
            </Typography>
            <Typography variant="body2" align="left" gutterBottom>
              - You are authorised to operate the nominated account; and
            </Typography>
            <Typography variant="body2" align="left" gutterBottom>
              - You are authorising in accordance with the account authority on your nominated account; and
            </Typography>
            <Typography variant="body2" align="left" gutterBottom>
              - You have understood and agreed to the terms and conditions set out in this Request and in your Direct Debit Request Service Agreement.
            </Typography>
          </Box>
        </Grid>
       
          <Button
            onClick={handleSubmitFundingAccount}
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 0, mb: 2, borderRadius: 3, width: '100%' }}
            disabled={isLoading}
          >
            <Typography>
              {isLoading ? 'Loading...' : 'CONTINUE'}
            </Typography>
          </Button>
        
          { footerLinks }
      </Box></>)}
    </Container></ThemeProvider>
  );
};

export default FundingAccounts;
